import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FactorAvailability, FactorAvailabilityStatus } from "@/types";

const COMPLETE_STATUSES: ReadonlySet<FactorAvailabilityStatus> = new Set([
  "available",
]);

const FACTOR_LABELS: Record<keyof FactorAvailability, string> = {
  search_volume: "Search volume",
  search_trend: "Search trend",
  hero_rank: "Hero rank",
  hero_relevance: "Hero relevance",
  asin_density: "ASIN density",
  keyword_sales: "Keyword sales",
};

const STATUS_LABELS: Record<FactorAvailabilityStatus, string> = {
  available: "available",
  computed_via_competitor_category: "computed from competitor category",
  unavailable: "unavailable",
  unavailable_no_js_coverage: "no Jungle Scout coverage",
  pending: "pending",
  unavailable_no_category: "no category found",
};

function getIncompleteFactors(
  availability: FactorAvailability,
): Array<{ label: string; statusLabel: string }> {
  return (Object.keys(availability) as Array<keyof FactorAvailability>)
    .filter((key) => !COMPLETE_STATUSES.has(availability[key]))
    .map((key) => ({
      label: FACTOR_LABELS[key],
      statusLabel: STATUS_LABELS[availability[key]],
    }));
}

interface IncompleteScoreBadgeProps {
  factorAvailability: FactorAvailability | null | undefined;
  pointsAvailable: number | null | undefined;
}

export function IncompleteScoreBadge({
  factorAvailability,
  pointsAvailable,
}: IncompleteScoreBadgeProps) {
  const isCapped = pointsAvailable != null && pointsAvailable < 100;
  const hasCompetitorCategory =
    factorAvailability?.asin_density === "computed_via_competitor_category";

  if (!isCapped && !hasCompetitorCategory) return null;

  const incompleteFactors =
    factorAvailability != null ? getIncompleteFactors(factorAvailability) : [];

  const label = isCapped
    ? `Reduced model — ${pointsAvailable}/100 pts`
    : "Score caveat";

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="warning" className="cursor-default">
            {label}
          </Badge>
        </TooltipTrigger>
        {incompleteFactors.length > 0 && (
          <TooltipContent side="bottom" className="max-w-[280px]">
            <ul className="space-y-1">
              {incompleteFactors.map(({ label: factorLabel, statusLabel }) => (
                <li key={factorLabel} className="text-xs">
                  <span className="font-medium">{factorLabel}:</span> {statusLabel}
                </li>
              ))}
            </ul>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
